<template>
  <b-modal
    v-model="showModal"
    id="address-modal"
    :title="`Customer's addresses`"
    cancel-variant="outline-secondary"
    @hidden="onModalHidden"
  >
    <div v-if="isContentLoading" class="text-center pt-3">
      <b-spinner label="Loading..." />
    </div>

    <div v-else>
      <div v-if="customerAddresses.length > 1" class="address-picker mb-2">
        <button
          v-for="address in formattedAddresses"
          :key="address.id"
          type="button"
          class="address-picker-row"
          :class="{ active: activeAddressId === address.id }"
          @click="selectAddress(address)"
        >
          <span class="address-picker-icon" aria-hidden="true">
            {{ activeAddressId === address.id ? '▾' : '▸' }}
          </span>
          <span class="address-picker-summary">
            {{ address.summary }}<span v-if="address.isDefault" class="address-default-star"> ★</span>
          </span>
        </button>
      </div>

      <p
        v-if="customerAddresses.length > 1 && !showEditForm"
        class="text-muted mb-0"
      >
        {{ allowSave ? 'Select an address to edit.' : 'Select an address to view.' }}
      </p>

      <b-form v-if="showEditForm">

        <b-form-group label="Country" label-for="address-country">
          <CountrySelect
            input-id="address-country"
            v-model="edited.country_id"
            :clearable="false"
            :disabled="!allowSave"
          />
          <small v-if="v$.edited?.country_id?.$error" class="text-danger">Required</small>
        </b-form-group>

        <b-form-group label="Region" label-for="address-region">
          <RegionSelect
            input-id="address-region"
            v-model="edited.region_id"
            :country-id="edited.country_id"
            :clearable="false"
            :disabled="!allowSave"
          />
          <small v-if="v$.edited?.region_id?.$error" class="text-danger">Required</small>
        </b-form-group>

        <b-form-group label="City" label-for="address-city">
          <b-form-input
            id="address-city"
            v-model="edited.city"
            :readonly="!allowSave"
            required
          />
          <small v-if="v$.edited?.city?.$error" class="text-danger">Required</small>
        </b-form-group>

        <b-form-group label="Address line 1" label-for="address_line_1">
          <b-form-input
            id="address_line_1"
            v-model="edited.address_line_1"
            :readonly="!allowSave"
            required
          />
          <small v-if="v$.edited?.address_line_1?.$error" class="text-danger">Required</small>
        </b-form-group>

        <b-form-group label="Address line 2" label-for="address_line_2">
          <b-form-input
            id="address_line_2"
            v-model="edited.address_line_2"
            :readonly="!allowSave"
          />
        </b-form-group>

        <b-form-group label="Zip code" label-for="address-zip">
          <b-form-input
            id="address-zip"
            v-model="edited.zip_code"
            :readonly="!allowSave"
            required
          />
          <small v-if="v$.edited?.zip_code?.$error" class="text-danger">Required</small>
        </b-form-group>

        <b-form-group label="Name" label-for="address-name">
          <b-form-input
            id="address-name"
            v-model="edited.name"
            placeholder="Enter customer name"
            :readonly="!allowSave"
            required
          />
          <small v-if="v$.edited?.name?.$error" class="text-danger">Required</small>
        </b-form-group>

        <b-form-group label="Phone number" label-for="address-phone">
          <b-form-input
            id="address-phone"
            v-model="edited.phone"
            type="tel"
            :readonly="!allowSave"
            required
          />
          <small v-if="v$.edited?.phone?.$error" class="text-danger">Required</small>
        </b-form-group>

        <b-form-group>
          <b-form-textarea
            v-model="edited.description"
            placeholder="Description"
            rows="3"
            max-rows="6"
            :readonly="!allowSave"
          />
        </b-form-group>
      </b-form>
    </div>

    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        @click="showModal = false"
      >
        Close
      </b-button>
      <b-button
        v-if="showEditForm && allowSave"
        variant="primary"
        :disabled="isFormLoading"
        @click="onSubmit"
      >
        <b-spinner v-if="isFormLoading" label="Spinning" small />
        <span v-else>Save</span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { toastSuccess, toastFailure } from '@/libs/toastification'
import CountrySelect from '@/views/Components/Elements/CountrySelect.vue'
import RegionSelect from '@/views/Components/Elements/RegionSelect.vue'

const cloneObject = (object) => JSON.parse(JSON.stringify(object))

const defaultAddress = {
  id: null,
  country_id: window.companyCountryId,
  region_id: null,
  city: '',
  address_line_1: '',
  address_line_2: '',
  zip_code: '',
  name: '',
  phone: '',
  description: '',
  default: true,
}

export default {
  name: 'Addresses',
  components: {
    CountrySelect,
    RegionSelect,
  },
  props: {
    customer: {
      type: Object,
      default: null,
    },
    clickDate: {
      type: Date,
      default: null,
    },
    allowSave: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    return {
      v$: useVuelidate(),
    }
  },
  validations() {
    return {
      edited: {
        name: { required },
        phone: { required },
        country_id: { required },
        city: { required },
        address_line_1: { required },
        zip_code: { required },
        region_id: { required },
      },
    }
  },
  data() {
    return {
      showModal: false,
      isContentLoading: false,
      isFormLoading: false,
      customerAddresses: [],
      edited: cloneObject(defaultAddress),
    }
  },
  computed: {
    activeAddressId() {
      return this.$store.state.addresses?.activeAddressId ?? null
    },
    countries() {
      return this.$store.getters['companies/countries'] || []
    },
    showEditForm() {
      if (this.customerAddresses.length === 0) {
        return true
      }

      if (this.customerAddresses.length === 1) {
        return true
      }

      return this.activeAddressId != null
    },
    formattedAddresses() {
      const countryList = this.countries || []

      return this.customerAddresses.map((address) => {
        const country = address?.country
          || countryList.find((item) => Number(item.id) === Number(address?.country_id))?.long_name
          || ''

        const isDefault = address.default === true || address.default === 1

        const summary = [
          address.address_line_1,
          address.city,
          address.region,
          country,
        ].filter(Boolean).join(', ')

        return {
          ...address,
          summary,
          isDefault,
        }
      })
    },
  },
  watch: {
    clickDate() {
      this.openAddressesModal()
    },
  },
  methods: {
    openAddressesModal() {
      if (!this.customer?.id) {
        return
      }

      this.customerAddresses = []
      this.isContentLoading = true
      this.showModal = true
      this.resetEdited()
      this.v$.$reset()
      this.$store.commit('addresses/CLEAR_ACTIVE_ADDRESS')

      Promise.all([
        this.ensureCountriesLoaded(),
        this.$store.dispatch('addresses/loadAddresses', this.customer.id),
      ])
        .then(([, addresses]) => {
          this.customerAddresses = Array.isArray(addresses) ? addresses : []
          this.initializeAddressView(this.customerAddresses)
        })
        .catch(() => {
          this.showModal = false
          toastFailure('Something went wrong.')
        })
        .finally(() => {
          this.isContentLoading = false
        })
    },
    fillEditedFromAddress(address) {
      this.edited.id = address.id
      this.edited.country_id = address.country_id ?? window.companyCountryId
      this.edited.name = address.name || ''
      this.edited.phone = address.phone || ''
      this.edited.region_id = address.region_id ?? null
      this.edited.city = address.city || ''
      this.edited.address_line_1 = address.address_line_1 || ''
      this.edited.address_line_2 = address.address_line_2 || ''
      this.edited.zip_code = address.zip_code || ''
      this.edited.description = address.description || ''
      this.edited.default = address.default ?? false
    },
    resetEdited() {
      this.edited = cloneObject(defaultAddress)
    },
    selectAddress(address) {
      this.$store.commit('addresses/SET_ACTIVE_ADDRESS', address.id)
      this.fillEditedFromAddress(address)
      this.v$.$reset()
    },
    initializeAddressView(addresses) {
      const list = Array.isArray(addresses) ? addresses : []

      if (!list.length) {
        this.resetEdited()
        return
      }

      if (list.length === 1) {
        this.selectAddress(list[0])
        return
      }

      this.resetEdited()
    },
    ensureCountriesLoaded() {
      if (this.countries.length) {
        return Promise.resolve()
      }

      return this.$store.dispatch('companies/loadCountries').catch(() => {})
    },
    onModalHidden() {
      this.$store.commit('addresses/CLEAR_ACTIVE_ADDRESS')
      this.customerAddresses = []
      this.isContentLoading = false
      this.resetEdited()
      this.v$.$reset()
    },
    async onSubmit(event) {
      if (event?.preventDefault) {
        event.preventDefault()
      }

      if (!this.showEditForm) {
        return
      }

      const isFormCorrect = await this.v$.$validate()
      if (!isFormCorrect) {
        return
      }

      this.submitForm()
    },
    submitForm() {
      this.isFormLoading = true
      this.edited.user_id = this.customer.id

      const action = this.edited.id != null
        ? this.$store.dispatch('addresses/updateAddress', { ...this.edited })
        : this.$store.dispatch('addresses/addAddress', { ...this.edited })

      action
        .then(() => {
          this.showModal = false
          toastSuccess(this.edited.id != null ? 'Address updated' : 'Address added')
        })
        .catch(() => {
          toastFailure(this.edited.id != null ? 'Failed to update address' : 'Failed to add the address')
        })
        .finally(() => {
          this.isFormLoading = false
        })
    },
  },
}
</script>

<style scoped>
.address-picker {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.address-picker-row {
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 0.75rem 0.85rem;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.address-picker-row:hover {
  background: #f8f8f8;
  border-color: #b9b9c3;
}

.address-picker-row.active {
  background: #e8e6fc;
  border-color: #7367f0;
}

.address-default-star {
  color: #7367f0;
  font-weight: 600;
}

.address-picker-icon {
  flex-shrink: 0;
  width: 1rem;
  margin-top: 0.1rem;
  margin-right: 0.65rem;
  color: #6e6b7b;
  font-size: 0.95rem;
  line-height: 1.4;
  text-align: center;
}

.address-picker-summary {
  line-height: 1.4;
}
</style>
